"use client";

import clsx from "clsx";
import { Loader2, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Control, Controller, Path } from "react-hook-form";

import IconArrowDown from "@/components/Icons/IconArrowDown";

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
  };
}

const CustomLoadingIndicator = () => (
  <div className="flex items-center justify-center px-2">
    <Loader2 className="h-4 w-4 animate-spin text-primary" />
  </div>
);

const LoadingMore = () => (
  <div className="flex items-center justify-center p-2 text-sm text-gray-500">
    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
    Loading more...
  </div>
);

export interface Option {
  label: string;
  value: any;
}

interface FormAutoCompleteFullScreenProps<T extends Record<string, any>> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  loadOptions?: (
    inputValue: string,
    page?: number,
    filter?: Record<string, string>,
  ) => Promise<PaginatedResponse<Option>>;
  displayOption?: (option: any) => Option;
  transformer?: (value: any) => any;
  onSelect?: (option: Option) => void;
  onClear?: () => void;
  placeholder?: string;
  isDisabled?: boolean;
  isClearable?: boolean;
  defaultOptions?: boolean | readonly Option[];
  customNoOptionsMessage?: () => string;
  dependencies?: Record<string, any>;
  shouldResetOnDependencyChange?: boolean;
  helperText?: string;
  hideIfNoData?: boolean;
}

const FormAutoCompleteFullScreen = <T extends Record<string, any>>({
  name,
  control,
  label,
  loadOptions: loadOptionsProp = async () => ({
    data: [],
    meta: { currentPage: 1, totalPages: 1, hasNextPage: false },
  }),
  displayOption,
  transformer,
  onSelect,
  onClear,
  placeholder,
  isDisabled = false,
  isClearable = true,
  defaultOptions = true,
  customNoOptionsMessage,
  dependencies = {},
  shouldResetOnDependencyChange = true,
  helperText,
  hideIfNoData,
  ...props
}: FormAutoCompleteFullScreenProps<T>) => {
  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [localValue, setLocalValue] = useState<Option | null>(null);

  // Search and options state
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentOptions, setCurrentOptions] = useState<Option[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [initialOptionsLoaded, setInitialOptionsLoaded] = useState(false);

  // Add debounce ref to prevent multiple requests
  const isLoadingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const prevDependenciesRef = useRef(dependencies);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Memoize default functions to prevent unnecessary re-renders
  const defaultDisplayOption = useCallback(
    (option: any): Option | null => {
      if (!option) return null;

      if (option?.label) {
        return option;
      }

      // If just a value is provided, try to find its label in current options
      const foundOption = currentOptions.find((o: any) => o.value === option);

      return {
        label: foundOption?.label || String(option),
        value: option.value !== undefined ? option.value : option,
      };
    },
    [currentOptions],
  );

  const getOptionDisplay = displayOption || defaultDisplayOption;
  const transformValue = useCallback(transformer || ((value: any): any => value), []);

  // Load initial options if defaultOptions is true
  useEffect(() => {
    const fetchInitialOptions = async () => {
      if (defaultOptions === true && !initialOptionsLoaded) {
        try {
          setIsLoading(true);
          const response = await loadOptionsProp("", 1, dependencies);
          setCurrentOptions(response.data);
          setHasNextPage(response.meta.hasNextPage);
          setInitialOptionsLoaded(true);
        } catch (error) {
          console.error("Error loading initial options:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchInitialOptions();
  }, [JSON.stringify(dependencies), defaultOptions, initialOptionsLoaded, loadOptionsProp]);

  // Watch for dependency changes
  useEffect(() => {
    const currentDepsJson = JSON.stringify(dependencies);
    const prevDepsJson = JSON.stringify(prevDependenciesRef.current);
    const dependenciesChanged = currentDepsJson !== prevDepsJson;

    if (dependenciesChanged && shouldResetOnDependencyChange) {
      // Reset options and refetch with new dependencies
      setCurrentOptions([]);
      setInitialOptionsLoaded(false);
      prevDependenciesRef.current = dependencies;

      // Immediately fetch new options with the updated dependencies if defaultOptions is true
      if (defaultOptions === true) {
        const fetchOptionsWithNewDeps = async () => {
          try {
            setIsLoading(true);
            const response = await loadOptionsProp("", 1, dependencies);
            setCurrentOptions(response.data);
            setHasNextPage(response.meta.hasNextPage);
            setInitialOptionsLoaded(true);
          } catch (error) {
            console.error("Error loading options after dependency change:", error);
          } finally {
            setIsLoading(false);
          }
        };

        fetchOptionsWithNewDeps();
      }
    }
  }, [JSON.stringify(dependencies), shouldResetOnDependencyChange, defaultOptions, loadOptionsProp]);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsDialogOpen(false);
  }, []);

  const handleConfirm = useCallback(
    (fieldOnChange: (value: any) => void) => {
      if (localValue) {
        const transformedValue = transformValue(localValue.value);
        fieldOnChange(transformedValue);
        onSelect?.(localValue);
      }
      closeDialog();
    },
    [localValue, transformValue, onSelect, closeDialog],
  );

  const handleClear = useCallback(
    (fieldOnChange: (value: any) => void) => {
      setLocalValue(null);
      fieldOnChange(null);
      onClear?.();
    },
    [onClear],
  );

  // Search function with debounce
  const handleSearch = useCallback(
    async (inputValue: string) => {
      if (isLoadingRef.current) {
        abortControllerRef.current?.abort();
      }

      abortControllerRef.current = new AbortController();
      isLoadingRef.current = true;
      setIsLoading(true);
      setSearchQuery(inputValue);

      try {
        const response = await loadOptionsProp(inputValue, 1, dependencies);
        if (!abortControllerRef.current.signal.aborted) {
          setCurrentOptions(response.data);
          setCurrentPage(1);
          setHasNextPage(response.meta.hasNextPage);
        }
      } catch (error) {
        if (!abortControllerRef.current.signal.aborted) {
          console.error("Error loading options:", error);
        }
      } finally {
        if (!abortControllerRef.current.signal.aborted) {
          setIsLoading(false);
          isLoadingRef.current = false;
        }
      }
    },
    [dependencies, loadOptionsProp],
  );

  // Load more options for infinite scrolling
  const loadMoreOptions = useCallback(async () => {
    if (!hasNextPage || isLoadingMore || isLoadingRef.current) return;

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const response = await loadOptionsProp(searchQuery, nextPage, dependencies);

      setCurrentOptions((prev) => [...prev, ...response.data]);
      setCurrentPage(nextPage);
      setHasNextPage(response.meta.hasNextPage);
    } catch (error) {
      console.error("Error loading more options:", error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [currentPage, hasNextPage, isLoadingMore, searchQuery, dependencies, loadOptionsProp]);

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    if (scrollHeight - scrollTop <= clientHeight + 50 && hasNextPage && !isLoadingMore) {
      loadMoreOptions();
    }
  }, [hasNextPage, isLoadingMore, loadMoreOptions]);

  if (!currentOptions.length && hideIfNoData) {
    return null;
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, formState: { isSubmitting }, fieldState: { error, isTouched } }) => {
        // Set local value from field value when dialog opens
        useEffect(() => {
          if (isDialogOpen && field.value) {
            setLocalValue(getOptionDisplay(field.value));
          }
        }, [isDialogOpen, field.value]);

        const displayText = field.value
          ? getOptionDisplay(field.value)?.label
          : placeholder || helperText || "Select an option";

        return (
          <div className="w-full">
            {label && <label className="inter mb-1 block text-sm font-semibold text-[#000000]">{label}</label>}

            {/* Trigger Button */}
            <div
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              aria-haspopup="dialog"
              aria-expanded={isDialogOpen}
              aria-label={`Select ${helperText || label || "option"}`}
              className={clsx(
                "flex items-center justify-between rounded-2xl border border-secondary-light bg-secondary-light p-3 transition-all duration-300 hover:cursor-pointer hover:border-grey-border",
                isDisabled && "pointer-events-none opacity-50",
              )}
              onClick={isDisabled ? undefined : openDialog}
              onKeyDown={(e) => {
                if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  openDialog();
                }
              }}
            >
              <div className="flex flex-1 items-center px-3 py-1">
                {field.value ? (
                  <div className="flex items-center">
                    <span className="inter font-medium text-[#000000]">{displayText}</span>
                    {isClearable && (
                      <button
                        className="ml-2 rounded-full p-1 hover:bg-gray-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClear(field.onChange);
                        }}
                        aria-label={`Clear ${displayText}`}
                      >
                        <X className="h-3 w-3 text-gray-500" />
                      </button>
                    )}
                  </div>
                ) : (
                  <span className="inter text-medium text-secondary-foreground">{displayText}</span>
                )}
              </div>
              <div className="flex items-center px-2">
                <IconArrowDown height="16" width="16" fill="hsla(var(--secondary-foreground))"></IconArrowDown>
              </div>
            </div>

            {error && <p className="inter mt-2 text-sm font-medium text-red-500">{error.message}</p>}

            {/* Full Screen Dialog */}
            {isDialogOpen && (
              <div className="fixed inset-0 z-100000 flex flex-col bg-white">
                <div className="flex items-center justify-between border-b p-4">
                  <h2 className="font-urbanist text-lg font-semibold">{helperText || label || "Select an option"}</h2>
                  <button
                    onClick={closeDialog}
                    className="rounded-full p-2 hover:bg-gray-100"
                    aria-label="Close dialog"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  <div className="sticky top-0 bg-white px-2 pt-4">
                    <div className="relative w-full">
                      <input
                        type="text"
                        className="w-full rounded-xl border p-3 hover:cursor-text focus:outline-none focus:ring-1 focus:ring-primary"
                        placeholder={`Search ${(helperText || label || "options").toLowerCase()}...`}
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        aria-label={`Search ${helperText || label || "options"}`}
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 hover:bg-gray-100"
                          onClick={() => {
                            setSearchQuery("");
                            handleSearch("");
                          }}
                          aria-label="Clear search"
                        >
                          <X className="h-4 w-4 text-gray-500" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div
                    ref={scrollContainerRef}
                    className="h-full overflow-y-auto"
                    onScroll={handleScroll}
                    role="listbox"
                  >
                    {isLoading ? (
                      <div className="flex h-32 items-center justify-center">
                        <CustomLoadingIndicator />
                      </div>
                    ) : currentOptions.length > 0 ? (
                      <>
                        {currentOptions.map((option) => {
                          const isSelected = localValue?.value === option.value;

                          return (
                            <div
                              key={String(option.value)}
                              role="option"
                              tabIndex={0}
                              aria-selected={isSelected}
                              className={clsx(
                                "inter cursor-pointer px-3 py-4",
                                isSelected ? "bg-secondary-light" : "hover:bg-gray-50",
                              )}
                              onClick={() => setLocalValue(option)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setLocalValue(option);
                                }
                              }}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-base font-medium">{option.label}</span>
                                {isSelected && (
                                  <svg className="h-5 w-5 text-primary" viewBox="0 0 20 20" fill="currentColor">
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {isLoadingMore && <LoadingMore />}
                      </>
                    ) : (
                      <div className="py-8 text-center">
                        <p className="text-secondary-foreground">
                          {customNoOptionsMessage?.() || "No options available"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t p-4">
                  <div className="flex gap-4">
                    {isClearable && (
                      <button
                        onClick={() => handleClear(field.onChange)}
                        className="inter flex-1 rounded-xl border bg-primary-light px-4 py-3 text-center font-medium text-primary"
                      >
                        Clear
                      </button>
                    )}
                    <button
                      onClick={() => handleConfirm(field.onChange)}
                      className="inter flex-1 rounded-xl bg-primary px-4 py-3 text-center font-medium text-white"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }}
    />
  );
};

export default FormAutoCompleteFullScreen;
